import { ApplicationConfigService } from './../../core/config/application-config.service';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { environment } from 'src/environments/environment';
import { SigninService } from './signin.service';
import { HttpClient } from '@angular/common/http';
import { MasterDataService } from 'src/app/core/service/master-data.service';
import { Login } from './login.model';
import { slideAnimation } from 'src/app/shared/animations/animations.animation';

@Component({
  selector: 'signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  animations: [slideAnimation],
  standalone: false,
})
export class SigninComponent implements OnInit, AfterViewInit {
  companyInfo = environment.companyInfo;
  tenantField = environment.tenantField;
  tenantId = environment.tenant;

  @ViewChild('username', { static: false })
  username!: ElementRef;

  authenticationError = false;
  employeeRoleType = 'ROLE_EMPLOYEE';
  isFirstTimeUser = false;
  passwordResetMessage = '';
  isLoading = false;
  showPassword: boolean = false;

  loginForm = this.fb.group({
    username: [null, [Validators.required]],
    password: [null, [Validators.required]],
    role: [null],
    tenantId: [null],
    rememberMe: [false],
  });

  currentIndex = 0;
  slides = [
    { image: this.companyInfo.slider1 },
    { image: this.companyInfo.slider2 },
  ];

  isLogin = true;

  constructor(
    private loginService: SigninService,
    private router: Router,
    private fb: FormBuilder,
    protected localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private http: HttpClient,
    private masterDataService: MasterDataService,
    private applicationConfigService: ApplicationConfigService
  ) {
    this.router.events.subscribe(event => {
      console.log('Router event:', event);
    });
  }

  ngOnInit(): void {
    this.localStorageService.clear('authenticationToken');
    this.sessionStorageService.clear('authenticationToken');

    this.preloadImages();
    this.startSlideShow();
  }

  ngAfterViewInit(): void {
    // this.username.nativeElement.focus();
  }

  preloadImages() {
    this.slides.forEach((slide) => {
      new Image().src = slide.image;
    });
  }

  setCurrentSlideIndex(index: number) {
    this.currentIndex = index;
  }

  isCurrentSlideIndex(index: number) {
    return this.currentIndex === index;
  }

  nextSlide() {
    this.currentIndex =
      this.currentIndex < this.slides.length - 1 ? ++this.currentIndex : 0;
  }

  startSlideShow() {
    setInterval(() => {
      this.nextSlide();
    }, 5000);
  }

  /**
   * Main login function
   */
  login(): void {
    // Reset states
    this.authenticationError = false;
    this.isFirstTimeUser = false;
    this.passwordResetMessage = '';
    this.isLoading = true;

    // --- Tenant handling ---
    if (
      this.tenantField === 'FREE_TEXT' &&
      !this.loginForm.get('tenantId')!.value
    ) {
      this.isLoading = false;
      return;
    }

    this.localStorageService.store(
      'tenantId',
      this.loginForm.get('tenantId')!.value
    );

    if (this.tenantField === 'FETCH_URL') {
      let hostname = window.location?.hostname;
      const parts = hostname.split('.');
      if (parts.length > 2) {
        this.localStorageService.store('tenantId', parts[0]);
        this.tenantId = parts[0];
      }
    }

    if (!this.tenantField || this.tenantField === 'NONE') {
      this.localStorageService.store('tenantId', this.tenantId);
    }

    // --- Build login credentials ---
    const username = this.loginForm.get('username')!.value || '';
    const password = this.loginForm.get('password')!.value || '';

    // Store credentials temporarily for password change flow
    this.sessionStorageService.store('tempUsername', username);
    this.sessionStorageService.store('tempPassword', password);

    // 🔹 First call /api/login/authenticate to check for password reset requirement
    this.authenticateUser(username, password);
  }

  /**
   * Call authenticate API to verify credentials & detect password reset requirement
   */
  private authenticateUser(username: string, password: string) {
    const payload = {
      userName: username,
      password: password,
      roleType: this.employeeRoleType,
    };

    this.http
      .post<any>(
        this.applicationConfigService.getJpaMicroServiceEndpointFor(
          'api/login/authenticate'
        ),
        payload
      )
      .subscribe({
        next: () => {
          // Authentication OK → continue normal login
          this.continueLogin(username, password);
        },
        error: (err) => {
          console.log('Authenticate API error:', err);
          this.isLoading = false;

          if (this.isPasswordResetRequired(err)) {
            console.log('🔐 First-time user detected for:', username);

            this.isFirstTimeUser = true;
            this.passwordResetMessage =
              'First-time login detected. You will be redirected to change your password.';

            // Show message for 2 seconds then redirect
            setTimeout(() => {
              console.log('➡️ Navigating to change-password for', username);

              this.router
                .navigate(['/change-password'], {
                  queryParams: {
                    username: username,
                    firstTime: 'true',
                  },
                })
                .then((success) => {
                  if (!success) {
                    const params = new URLSearchParams({
                      username: username,
                      firstTime: 'true',
                    });
                    window.location.href = `change-password?${params.toString()}`;
                  }
                });
            }, 2000);
          } else if (err.status === 0 || err.status >= 500) {
            this.authenticationError = true;
            this.passwordResetMessage = 'Failed to connect. Please check your internet connection and try again.';
            this.sessionStorageService.clear('tempUsername');
            this.sessionStorageService.clear('tempPassword');
          } else {
            this.authenticationError = true;
            this.passwordResetMessage =
              'Invalid username or password. Please try again.';
            this.sessionStorageService.clear('tempUsername');
            this.sessionStorageService.clear('tempPassword');
          }
        },
      });
  }

  /**
   * Continue normal login after authenticate API passes
   */
  private continueLogin(username: string, password: string): void {
    this.loginService
      .login({
        userName: username,
        password: password,
        roleType: this.employeeRoleType,
        rememberMe: true,
      })
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.authenticationError = false;

          // Clear temporary credentials on successful login
          this.sessionStorageService.clear('tempUsername');
          this.sessionStorageService.clear('tempPassword');

          const token =
            this.localStorageService.retrieve('authenticationToken') ??
            this.sessionStorageService.retrieve('authenticationToken');

          if (token) {
            this.http
              .get(
                this.applicationConfigService.getJpaMicroServiceEndpointFor(
                  'api/login/master-data'
                ),
                { headers: { Authorization: token } }
              )
              .subscribe({
                next: (masterData) => {
                  this.masterDataService.setMasterData(masterData);
                  // compute sensible defaults for customer/site/contract selection
                  try {
                    const md: any = masterData || {};
                    const sel: any = {};

                    // prefer explicit user assignment if present
                    if (md.user) {
                      if (md.user.customerId != null) sel.customerId = Number(md.user.customerId);
                      if (md.user.siteId != null) sel.siteId = Number(md.user.siteId);
                      if (md.user.contractId != null) sel.contractId = Number(md.user.contractId);
                    }

                    // fallback: if only one customer exists, pick it
                    const customers = (md.customers || []).slice();
                    if (sel.customerId == null && customers.length === 1) {
                      sel.customerId = customers[0].customerId ?? customers[0].id;
                    }

                    // derive site if single site under chosen customer
                    if (sel.siteId == null && sel.customerId != null) {
                      const cust = customers.find((c: any) => c.customerId == sel.customerId || c.id == sel.customerId);
                      const sites = (cust?.sites || []).slice();
                      if (sites.length === 1) sel.siteId = sites[0].siteId ?? sites[0].id;
                    }

                    // derive contract if single contract under chosen site
                    if (sel.contractId == null && sel.siteId != null) {
                      // search across customers for the site
                      const site = (customers || []).flatMap((c: any) => c.sites || []).find((s: any) => s.siteId == sel.siteId || s.id == sel.siteId);
                      const contracts = (site?.contracts || []).slice();
                      if (contracts.length === 1) sel.contractId = contracts[0].contractId ?? contracts[0].id;
                    }

                    // Only store selected ids for Site Manager (SM) users
                    const roleLooksLikeSM = (val: any): boolean => {
                      if (val == null) return false;
                      const s = String(val).replace(/[^A-Za-z0-9_ ]/g, '').toUpperCase();
                      if (!s) return false;
                      if (s === 'SM' || s === 'ROLE_SM') return true;
                      if (s.includes('SITE') && s.includes('MANAGER')) return true;
                      if (s.includes('SITE_MANAGER') || s.includes('ROLE_SITE_MANAGER')) return true;
                      // also allow variations like 'SITE MANAGER', 'SITE-MANAGER'
                      return false;
                    };

                    let isSM = false;
                    try {
                      // Log the user/employee objects so we can inspect where roleCode is present
                      console.log('Signin: masterData.user =', md.user);
                      console.log('Signin: masterData.employee =', md.employee);

                      // First check explicit roleCode on employee then user
                      if (md.employee && md.employee.role && md.employee.role.roleCode != null) {
                        const rc = String(md.employee.role.roleCode).trim();
                        console.log('Signin: masterData.employee.role.roleCode =', rc);
                        if (roleLooksLikeSM(rc)) isSM = true;
                      }

                      if (!isSM && md.user && md.user.roleCode != null) {
                        const rc = String(md.user.roleCode).trim();
                        console.log('Signin: masterData.user.roleCode =', rc);
                        if (roleLooksLikeSM(rc)) isSM = true;
                      }

                      // If still not found, deeply scan employee then user objects for likely role identifiers
                      if (!isSM) {
                        const scanTargets = [] as any[];
                        if (md.employee) scanTargets.push(md.employee);
                        if (md.user) scanTargets.push(md.user);

                        const candidates: any[] = [];
                        const collect = (obj: any, depth = 0) => {
                          if (obj == null || depth > 5) return;
                          if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
                            candidates.push(obj);
                            return;
                          }
                          if (Array.isArray(obj)) {
                            for (const it of obj) collect(it, depth + 1);
                            return;
                          }
                          if (typeof obj === 'object') {
                            for (const k of Object.keys(obj)) collect(obj[k], depth + 1);
                            return;
                          }
                        };

                        for (const t of scanTargets) collect(t, 0);

                        for (const c of candidates) {
                          if (roleLooksLikeSM(c)) {
                            isSM = true;
                            break;
                          }
                        }
                      }

                      // Fallback: check stored USER_ROLE
                      if (!isSM) {
                        const roles = this.localStorageService.retrieve('USER_ROLE') || this.sessionStorageService.retrieve('USER_ROLE') || [];
                        const arr = Array.isArray(roles) ? roles : [roles];
                        if (arr.some((r: any) => roleLooksLikeSM(r))) isSM = true;
                      }
                    } catch (e) {
                      isSM = false;
                    }

                    if (isSM) {
                      // store selected ids so dropdowns can auto-populate
                      console.log('Signin: detected SM role — saving selected ids', sel);
                      this.masterDataService.setSelectedIds(sel);
                    } else {
                      // ensure we don't accidentally persist selections for other roles
                      console.log('Signin: not SM — skipping selected ids save', { sel, roles: this.localStorageService.retrieve('USER_ROLE') });
                    }
                  } catch (e) {
                    // ignore defaulting errors
                  }

                  this.router.navigate(['home']);
                },
                error: () => {
                  this.router.navigate(['home']);
                },
              });
          } else {
            this.router.navigate(['home']);
          }
        },
        error: (err) => {
          this.isLoading = false;
          console.log('Login error:', err);

          // Optional second check for password reset requirement here
          if (this.isPasswordResetRequired(err)) {
            this.router.navigate(['/change-password'], {
              queryParams: {
                username: username,
                firstTime: 'true',
              },
            });
          } else if (err.status === 0 || err.status >= 500) {
            this.authenticationError = true;
            this.passwordResetMessage = 'Failed to connect. Please check your internet connection and try again.';
            this.sessionStorageService.clear('tempUsername');
            this.sessionStorageService.clear('tempPassword');
          } else {
            this.authenticationError = true;
            this.passwordResetMessage =
              'Invalid username or password. Please try again.';
            this.sessionStorageService.clear('tempUsername');
            this.sessionStorageService.clear('tempPassword');
          }
        },
      });
  }

  /**
   * Check if the error indicates password reset is required
   */
  private isPasswordResetRequired(err: any): boolean {
    return (
      (err.status === 403 || err.error?.status === 'FORBIDDEN') &&
      (err.error?.message?.includes('Password reset required') ||
        err.error?.message === 'Password reset required before login')
    );
  }

  validateUser(): void {
    const username = this.loginForm.get('username')!.value || '';
    const password = this.loginForm.get('password')!.value || '';

    if (!username || !password) {
      this.authenticationError = true;
      this.passwordResetMessage = 'Please enter both username and password.';
      return;
    }

    this.login();
  }

  forgotPassword(): void {
    let username = this.loginForm.get('username')!.value;
    if (username) {
      let credentials = new Login(username, '', this.employeeRoleType, false);

      this.loginService.forgotPassword(credentials).subscribe(
        () => {
          this.isLogin = true;
          this.passwordResetMessage = 'Password reset link sent to your email.';
        },
        (error) => {
          console.error('Error occurred:', error);
          this.passwordResetMessage = 'User not found or error occurred.';
        }
      );
    } else {
      this.passwordResetMessage = 'Please enter your username first.';
    }
  }

  onInputChange(): void {
    if (this.authenticationError || this.passwordResetMessage) {
      this.authenticationError = false;
      this.passwordResetMessage = '';
    }
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.validateUser();
    } else {
      this.authenticationError = true;
      this.passwordResetMessage = 'Please fill in all required fields.';
    }
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }
}
